import { offlineStorage, type PendingAction } from './offlineStorage'
import { useApiClient } from '@siclop/kstone-vue'

export interface SyncResult {
  success: boolean
  synced: number
  failed: number
  errors: Array<{ action: PendingAction; error: string }>
}

/**
 * Sync manager for handling offline actions synchronization
 */
class SyncManager {
  private isSyncing = false
  private syncListeners: Array<(result: SyncResult) => void> = []

  /**
   * Register a listener for sync completion
   */
  onSyncComplete(callback: (result: SyncResult) => void): () => void {
    this.syncListeners.push(callback)
    return () => {
      this.syncListeners = this.syncListeners.filter(cb => cb !== callback)
    }
  }

  /**
   * Notify all listeners of sync result
   */
  private notifyListeners(result: SyncResult): void {
    this.syncListeners.forEach(cb => cb(result))
  }

  /**
   * Check if currently syncing
   */
  get syncing(): boolean {
    return this.isSyncing
  }

  /**
   * Sync all pending actions
   */
  async syncPendingActions(): Promise<SyncResult> {
    if (this.isSyncing) {
      return { success: false, synced: 0, failed: 0, errors: [] }
    }

    if (!navigator.onLine) {
      return { success: false, synced: 0, failed: 0, errors: [] }
    }

    this.isSyncing = true
    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: []
    }

    try {
      const pendingActions = await offlineStorage.getPendingActions()

      for (const action of pendingActions) {
        try {
          await this.executeAction(action)
          await offlineStorage.removePendingAction(action.id)
          result.synced++
        } catch (error) {
          result.failed++
          result.success = false
          result.errors.push({
            action,
            error: error instanceof Error ? error.message : 'Unknown error'
          })
          // Continue with next action instead of stopping
        }
      }
    } catch {
      result.success = false
    } finally {
      this.isSyncing = false
      this.notifyListeners(result)
    }

    return result
  }

  /**
   * Execute a single pending action
   */
  private async executeAction(action: PendingAction): Promise<void> {
    // Le client de l'application : adresse de l'API, cookie de session HttpOnly, en-tête anti-CSRF et
    // rafraîchissement sur 401. Plus de jeton lu dans le localStorage (il n'y est plus).
    const http = useApiClient().getAxiosInstance()

    switch (action.type) {
      case 'create':
        await http.post(action.endpoint, action.data)
        break
      case 'update':
        await http.put(action.endpoint, action.data)
        break
      case 'delete':
        await http.delete(action.endpoint)
        break
    }
  }

  /**
   * Queue an action for later sync. `endpoint` est relatif à l'adresse de l'API (ex. `/projects/12`), comme
   * pour les services.
   */
  async queueAction(
    type: 'create' | 'update' | 'delete',
    entity: string,
    endpoint: string,
    data?: unknown
  ): Promise<string> {
    return offlineStorage.addPendingAction({
      type,
      entity,
      endpoint,
      data
    })
  }

  /**
   * Get number of pending actions
   */
  async getPendingCount(): Promise<number> {
    return offlineStorage.getPendingActionsCount()
  }

  /**
   * Clear all pending actions (use with caution)
   */
  async clearPending(): Promise<void> {
    await offlineStorage.clearPendingActions()
  }

  /**
   * Register background sync (if supported)
   */
  async registerBackgroundSync(): Promise<boolean> {
    if ('serviceWorker' in navigator && 'sync' in ServiceWorkerRegistration.prototype) {
      try {
        const registration = await navigator.serviceWorker.ready
        await (registration as ServiceWorkerRegistration & { sync: { register: (tag: string) => Promise<void> } }).sync.register('sync-pending-actions')
        return true
      } catch {
        return false
      }
    }
    return false
  }
}

export const syncManager = new SyncManager()

// Auto-sync when coming back online
if (typeof window !== 'undefined') {
  window.addEventListener('online', async () => {
    // Wait a moment for connection to stabilize
    await new Promise(resolve => setTimeout(resolve, 1000))
    const pendingCount = await syncManager.getPendingCount()
    if (pendingCount > 0) {
      await syncManager.syncPendingActions()
    }
  })
}

export default syncManager
